import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, PlusCircle, MapPin, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";
import { LocationPicker } from "@/components/LocationPicker";
import { TriStateToggle } from "@/components/TriStateToggle";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";

const shelterSchema = z.object({
  name: z.string().trim().min(2, "Nazwa musi mieć min. 2 znaki").max(200, "Nazwa może mieć max. 200 znaków"),
  city: z.string().trim().min(2, "Miasto musi mieć min. 2 znaki").max(100, "Miasto może mieć max. 100 znaków"),
  region: z.string().trim().max(100, "Region może mieć max. 100 znaków").optional(),
  address: z.string().trim().max(300, "Adres może mieć max. 300 znaków").optional(),
  postal_code: z
    .string()
    .trim()
    .refine((val) => {
      if (!val) return true;
      return /^\d{2}-\d{3}$/.test(val);
    }, "Kod pocztowy musi mieć format XX-XXX")
    .optional()
    .or(z.literal("")),
  lat: z.number().min(49, "Szerokość geogr. musi być w Polsce (49-55)").max(55, "Szerokość geogr. musi być w Polsce (49-55)"),
  lng: z.number().min(14, "Długość geogr. musi być w Polsce (14-24)").max(24, "Długość geogr. musi być w Polsce (14-24)"),
  phone: z.string().trim().max(50, "Telefon może mieć max. 50 znaków").optional(),
  email: z.string().trim().email("Nieprawidłowy email").max(255).optional().or(z.literal("")),
  website: z
    .string()
    .trim()
    .max(500)
    .refine((val) => {
      if (!val) return true;
      return /^(https?:\/\/|www\.)[^\s]+\.[^\s]+$/.test(val);
    }, "Nieprawidłowy URL (użyj formatu https://... lub www...)")
    .optional()
    .or(z.literal("")),
  description: z.string().trim().max(2000, "Opis może mieć max. 2000 znaków").optional(),
  current_needs: z.string().trim().max(1000, "Potrzeby mogą mieć max. 1000 znaków").optional(),
  volunteer_info: z.string().trim().max(1000, "Info o wolontariacie może mieć max. 1000 znaków").optional(),
});

interface FormData {
  name: string;
  city: string;
  region: string;
  address: string;
  postal_code: string;
  lat: string;
  lng: string;
  phone: string;
  email: string;
  website: string;
  current_needs: string;
  urgent_needs: boolean;
  dog_walking_available: boolean | null;
  volunteering_available: boolean | null;
  dogs_available: boolean | null;
  cats_available: boolean | null;
  volunteer_info: string;
  description: string;
}

interface AddShelterFormProps {
  onSuccess?: () => void;
}

export const AddShelterForm = ({ onSuccess }: AddShelterFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { reverseGeocode, isLoading: isReverseGeocoding } = useReverseGeocode();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<FormData>({
    name: "",
    city: "",
    region: "",
    address: "",
    postal_code: "",
    lat: "",
    lng: "",
    phone: "",
    email: "",
    website: "",
    description: "",
    current_needs: "",
    volunteer_info: "",
    urgent_needs: false,
    dog_walking_available: null,
    volunteering_available: null,
    dogs_available: null,
    cats_available: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: keyof FormData, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const lat = parseFloat(formData.lat);
    const lng = parseFloat(formData.lng);

    // Walidacja lokalizacji
    if (!formData.lat || !formData.lng || isNaN(lat) || isNaN(lng)) {
      setErrors({ location: "Wskaż lokalizację schroniska na mapie" });
      toast.error("Wskaż lokalizację na mapie");
      return;
    }

    // Sprawdź czy współrzędne są w Polsce
    if (lat < 49 || lat > 55 || lng < 14 || lng > 24) {
      setErrors({ location: "Lokalizacja musi być w Polsce" });
      toast.error("Lokalizacja musi być w Polsce");
      return;
    }

    // Validate form data
    const dataToValidate = {
      name: formData.name,
      city: formData.city,
      region: formData.region || undefined,
      address: formData.address || undefined,
      postal_code: formData.postal_code || undefined,
      lat,
      lng,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      website: formData.website || undefined,
      description: formData.description || undefined,
      current_needs: formData.current_needs || undefined,
      volunteer_info: formData.volunteer_info || undefined,
    };

    const result = shelterSchema.safeParse(dataToValidate);
    
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast.error("Popraw błędy w formularzu");
      return;
    }

    setIsLoading(true);
    try {
      const websiteValue = formData.website.trim()
        ? formData.website.trim().toLowerCase().startsWith("www.")
          ? "https://" + formData.website.trim()
          : formData.website.trim()
        : null;

      const { error } = await supabase.from("shelters").insert({
        name: formData.name.trim(),
        city: formData.city.trim(),
        region: formData.region.trim() || null,
        address: formData.address.trim() || null,
        postal_code: formData.postal_code.trim() || null,
        lat,
        lng,
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        website: websiteValue,
        description: formData.description.trim() || null,
        current_needs: formData.current_needs.trim() || null,
        volunteer_info: formData.volunteer_info.trim() || null,
        urgent_needs: formData.urgent_needs,
        dog_walking_available: formData.dog_walking_available,
        volunteering_available: formData.volunteering_available,
        dogs_available: formData.dogs_available,
        cats_available: formData.cats_available,
        data_verified: true, // Admin adds directly so it's verified
      });

      if (error) throw error;

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelters-for-verification"] });

      toast.success("Schronisko dodane pomyślnie!");
      setIsSubmitted(true);
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || "Błąd dodawania schroniska");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      city: "",
      region: "",
      address: "",
      postal_code: "",
      lat: "",
      lng: "",
      phone: "",
      email: "",
      website: "",
      description: "",
      current_needs: "",
      volunteer_info: "",
      urgent_needs: false,
      dog_walking_available: null,
      volunteering_available: null,
      dogs_available: null,
      cats_available: null,
    });
    setErrors({});
    setIsSubmitted(false);
  };

  if (isSubmitted) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="font-display font-bold text-xl mb-2">Schronisko dodane!</h2>
          <p className="text-muted-foreground mb-6">
            Schronisko zostało dodane bezpośrednio do bazy danych.
          </p>
          <Button onClick={handleReset}>
            <PlusCircle className="w-4 h-4 mr-2" />
            Dodaj kolejne
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PlusCircle className="w-5 h-5" />
          Dodaj nowe schronisko
        </CardTitle>
        <CardDescription>
          Schronisko zostanie dodane bezpośrednio do bazy (bez oczekiwania na akceptację)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold">Dane podstawowe</h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nazwa schroniska *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="np. Schronisko dla Zwierząt"
                  required
                />
                {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Miasto *</Label>
                <AddressAutocomplete
                  id="city"
                  value={formData.city}
                  onChange={(value) => handleChange("city", value)}
                  onSuggestionSelect={(suggestion) => {
                    handleChange("city", suggestion.city);
                    if (suggestion.region) {
                      handleChange("region", suggestion.region);
                    }
                    if (suggestion.postalCode) {
                      handleChange("postal_code", suggestion.postalCode);
                    }
                    if (suggestion.lat && suggestion.lng) {
                      handleChange("lat", suggestion.lat.toFixed(6));
                      handleChange("lng", suggestion.lng.toFixed(6));
                    }
                  }}
                  placeholder="np. Warszawa"
                  required
                  type="city"
                />
                {errors.city && <p className="text-xs text-destructive">{errors.city}</p>}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="region">Województwo</Label>
                <Input
                  id="region"
                  value={formData.region}
                  onChange={(e) => handleChange("region", e.target.value)}
                  placeholder="np. mazowieckie"
                />
                {errors.region && <p className="text-xs text-destructive">{errors.region}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Ulica</Label>
                <AddressAutocomplete
                  id="address"
                  value={formData.address}
                  onChange={(value) => handleChange("address", value)}
                  onSuggestionSelect={(suggestion) => {
                    if (suggestion.address) {
                      handleChange("address", suggestion.address);
                    }
                    if (suggestion.city) {
                      handleChange("city", suggestion.city);
                    }
                    if (suggestion.region) {
                      handleChange("region", suggestion.region);
                    }
                    if (suggestion.postalCode) {
                      handleChange("postal_code", suggestion.postalCode);
                    }
                    if (suggestion.lat && suggestion.lng) {
                      handleChange("lat", suggestion.lat.toFixed(6));
                      handleChange("lng", suggestion.lng.toFixed(6));
                    }
                  }}
                  placeholder="np. Słoneczna"
                  type="address"
                  filterByCity={formData.city}
                />
                {errors.address && <p className="text-xs text-destructive">{errors.address}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="postal_code">Kod pocztowy</Label>
              <Input
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => handleChange("postal_code", e.target.value)}
                placeholder="np. 00-001"
                maxLength={6}
                className="max-w-[140px]"
              />
              {errors.postal_code && <p className="text-xs text-destructive">{errors.postal_code}</p>}
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              Lokalizacja na mapie
            </h3>

            <p className="text-sm text-muted-foreground">
              Kliknij na mapę lub przeciągnij pinezkę, aby wskazać lokalizację schroniska.
            </p>

            {errors.location && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-sm text-destructive">{errors.location}</p>
              </div>
            )}

            <LocationPicker
              lat={formData.lat ? parseFloat(formData.lat) : null}
              lng={formData.lng ? parseFloat(formData.lng) : null}
              onLocationSelect={(lat, lng) => {
                setFormData((prev) => ({
                  ...prev,
                  lat: lat.toString(),
                  lng: lng.toString(),
                }));
                setErrors((prev) => ({ ...prev, lat: "", lng: "", location: "" }));
              }}
              onReverseGeocode={async (lat, lng) => {
                const result = await reverseGeocode(lat, lng);
                if (result) {
                  setFormData((prev) => ({
                    ...prev,
                    city: result.city || prev.city,
                    region: result.region || prev.region,
                    address: result.address || prev.address,
                    postal_code: result.postalCode || prev.postal_code,
                  }));
                  toast.success("Adres uzupełniony z mapy");
                }
              }}
            />
            {isReverseGeocoding && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Pobieranie adresu z mapy...
              </p>
            )}
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-semibold">Dane kontaktowe</h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="np. +48 123 456 789"
                />
                {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email schroniska</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="np. kontakt@schronisko.pl"
                />
                {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Strona internetowa</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => handleChange("website", e.target.value)}
                placeholder="np. https://schronisko.pl"
              />
              {errors.website && <p className="text-xs text-destructive">{errors.website}</p>}
            </div>
          </div>

          {/* Needs section */}
          <div className="space-y-4">
            <h3 className="font-semibold">Potrzeby schroniska</h3>

            <div className="border rounded-lg p-4 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="current_needs">Aktualne potrzeby</Label>
                <Textarea
                  id="current_needs"
                  value={formData.current_needs}
                  onChange={(e) => handleChange("current_needs", e.target.value)}
                  placeholder="Czego aktualnie potrzebuje schronisko..."
                  rows={5}
                />
                {errors.current_needs && <p className="text-xs text-destructive">{errors.current_needs}</p>}
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <div className="space-y-0.5">
                  <Label htmlFor="urgent_needs" className="cursor-pointer">
                    Pilne potrzeby
                  </Label>
                  <p className="text-xs text-muted-foreground">Oznaczenie widoczne przez 30 dni</p>
                </div>
                <Switch
                  id="urgent_needs"
                  checked={formData.urgent_needs}
                  onCheckedChange={(checked) => handleChange("urgent_needs", checked)}
                />
              </div>
            </div>
          </div>

          {/* Volunteering section */}
          <div className="space-y-4">
            <h3 className="font-semibold">Wolontariat</h3>

            <div className="border rounded-lg p-4 space-y-4">
              <p className="text-xs text-muted-foreground -mt-1">Nie masz pewności? Zostaw „brak informacji"</p>

              <div className="grid gap-3">
                <TriStateToggle
                  id="volunteering"
                  label="Wolontariat możliwy"
                  value={formData.volunteering_available}
                  onChange={(val) => handleChange("volunteering_available", val)}
                />
                <TriStateToggle
                  id="dog_walking"
                  label="Spacery z psami"
                  value={formData.dog_walking_available}
                  onChange={(val) => handleChange("dog_walking_available", val)}
                />
              </div>

              <div className="space-y-2 pt-2 border-t">
                <Label htmlFor="volunteer_info">Dodatkowe informacje</Label>
                <Textarea
                  id="volunteer_info"
                  value={formData.volunteer_info}
                  onChange={(e) => handleChange("volunteer_info", e.target.value)}
                  placeholder="Jak zostać wolontariuszem, godziny spacerów..."
                  rows={5}
                />
                {errors.volunteer_info && <p className="text-xs text-destructive">{errors.volunteer_info}</p>}
              </div>
            </div>
          </div>

          {/* Adoption section */}
          <div className="space-y-4">
            <h3 className="font-semibold">Adopcje</h3>

            <div className="border rounded-lg p-4 space-y-3">
              <p className="text-xs text-muted-foreground -mt-1">Nie masz pewności? Zostaw „brak informacji"</p>

              <div className="grid gap-3">
                <TriStateToggle
                  id="dogs"
                  label="Psy do adopcji"
                  value={formData.dogs_available}
                  onChange={(val) => handleChange("dogs_available", val)}
                />
                <TriStateToggle
                  id="cats"
                  label="Koty do adopcji"
                  value={formData.cats_available}
                  onChange={(val) => handleChange("cats_available", val)}
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-4">
            <h3 className="font-semibold">Opis</h3>
            <div className="space-y-2">
              <Label htmlFor="description">Opis schroniska</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Krótki opis schroniska..."
                rows={4}
              />
              {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
            </div>
          </div>

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <PlusCircle className="w-4 h-4 mr-2" />
            )}
            Dodaj schronisko
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
